import { compact, concat, includes, keyBy, map, mapValues, omit, pickBy, sortBy, uniq, uniqBy, values, xor } from "lodash";
import { ClientSearchRoomHistory } from "../../proto/ClientSearchRoomHistory_pb";
import { Room, RoomMessage, RoomMessageStatus, RoomMessageType } from "../../proto/Global_pb";
import { RoomMediaType, RoomMessagesStateType } from "./types";
import { sharedMediaTypes } from "../../constants/globals";
import { toTime } from "../../libs/date";
import { createReducer } from "@reduxjs/toolkit";
import {
  addMessagesLoader,
  addRoomMedia,
  addRoomMessages,
  clearRoomMessages,
  clearRoomSelectedMessages,
  editMessagesLoader,
  editRoomMessage,
  markAsReadMessages,
  removeFakeMessage,
  removeMessagesLoader,
  removeMessagesLoaders,
  removeRoomMedia,
  removeRoomMessage,
  removeRoomMessages,
  resetSharedMedia,
  setScrolledMessage,
  setSharedMedia,
  updateRoomSelectedMessages,
} from "./actions";

const initialState: RoomMessagesStateType = {
  messages: {},
  roomMessages: {},
  roomSelectedMessages: [],
  roomMedia: {},
  messagesLoader: {},
  scrolledMessage: {},
  sharedMedia: {
    messages: {
      [sharedMediaTypes.SharedImages]: [],
      [sharedMediaTypes.SharedVideo]: [],
      [sharedMediaTypes.SharedAudio]: [],
      [sharedMediaTypes.SharedGif]: [],
      [sharedMediaTypes.SharedVoice]: [],
      [sharedMediaTypes.SharedUrl]: [],
      [sharedMediaTypes.SharedFile]: [],
    },
  },
};

const filterMediaMessages = (messages: Array<RoomMessage.AsObject | undefined>, filter: ClientSearchRoomHistory.FilterMap[keyof ClientSearchRoomHistory.FilterMap]) => {
  return messages.filter(
    (m) =>
      m?.deleted === false &&
      includes(
        {
          [ClientSearchRoomHistory.Filter.IMAGE]: [RoomMessageType.IMAGE, RoomMessageType.IMAGE_TEXT],
          [ClientSearchRoomHistory.Filter.VIDEO]: [RoomMessageType.VIDEO, RoomMessageType.VIDEO_TEXT],
          [ClientSearchRoomHistory.Filter.AUDIO]: [RoomMessageType.AUDIO, RoomMessageType.AUDIO_TEXT],
          [ClientSearchRoomHistory.Filter.VOICE]: [RoomMessageType.VOICE],
          [ClientSearchRoomHistory.Filter.GIF]: [RoomMessageType.GIF, RoomMessageType.GIF_TEXT],
          [ClientSearchRoomHistory.Filter.FILE]: [RoomMessageType.FILE, RoomMessageType.FILE_TEXT],
          [ClientSearchRoomHistory.Filter.URL]: [],
        }[filter],
        m.messageType
      )
  );
};

export const messagesReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(addRoomMessages, (state, action) => {
      const roomMedia = state.roomMedia[action.payload.roomId] || {};
      values(ClientSearchRoomHistory.Filter).forEach((filter) => {
        roomMedia[filter] = uniq(
          sortBy(
            compact(
              concat(
                state.roomMedia[action.payload.roomId]?.[filter],
                map(filterMediaMessages(map(action.payload.messageList, "forwardFrom"), filter), "messageId"),
                map(filterMediaMessages(map(action.payload.messageList, "replyTo"), filter), "messageId"),
                map(filterMediaMessages(action.payload.messageList, filter), "messageId")
              )
            )
          )
        );
      });
      state.messages = {
        ...state.messages,
        ...keyBy(
          uniq(
            compact(
              concat(
                map(action.payload.messageList, "forwardFrom"),
                // map(action.payload.messageList, "replyTo"),
                action.payload.messageList.map((message, index, messageList) => {
                  return {
                    ...message,
                    dateLastMessage: messageList[index - 1] && toTime(messageList[index - 1].createTime, "", "YYYY-MM-DD") !== toTime(message.createTime, "", "YYYY-MM-DD"),
                  };
                })
              )
            )
          ),
          "messageId"
        ),
      };
      state.roomMessages[action.payload.roomId] = uniq(sortBy(compact(concat(state.roomMessages[action.payload.roomId], map(action.payload.messageList, "messageId")))));
      state.roomMedia[action.payload.roomId] = roomMedia;
    })
    .addCase(editRoomMessage, (state, action) => {
      if (state.messages[action.payload.messageId] === undefined) return;
      state.messages[action.payload.messageId] = {
        ...state.messages[action.payload.messageId],
        ...action.payload.message,
      };
    })
    .addCase(markAsReadMessages, (state, action) => {
      const messagesId = state.roomMessages[action.payload.roomId]?.filter((id) => id <= action.payload.messageId);
      const pickedMessages = pickBy(
        state.messages,
        (msg) => includes(messagesId, msg.messageId) && !includes([RoomMessageStatus.LISTENED, RoomMessageStatus.SEEN], msg.status) && msg.author?.hash !== action.payload.updaterAuthorHash
      );
      state.messages = { ...state.messages, ...mapValues(pickedMessages, (msg) => ({ ...msg, status: RoomMessageStatus.SEEN })) };
    })
    .addCase(removeRoomMessage, (state, action) => {
      state.messages = omit(state.messages, action.payload.messageId);
      state.roomMessages[action.payload.roomId] = state.roomMessages[action.payload.roomId]?.filter((v) => v !== action.payload.messageId);
    })
    .addCase(removeRoomMessages, (state, action) => {
      state.messages = omit(state.messages, state.roomMessages[action.payload.roomId]);
      state.roomMessages = omit(state.roomMessages, action.payload.roomId);
    })
    .addCase(clearRoomMessages, (state, action) => {
      state.roomMessages[action.payload.roomId] = state.roomMessages[action.payload.roomId]?.filter((id) => id > action.payload.clearId);
      state.roomMedia[action.payload.roomId] = mapValues(state.roomMedia[action.payload.roomId], (mediaGroup) => mediaGroup?.filter((messageId) => messageId > action.payload.clearId));
    })
    .addCase(updateRoomSelectedMessages, (state, action) => {
      state.roomSelectedMessages = sortBy(xor(state.roomSelectedMessages, [action.payload.messageId]));
    })
    .addCase(clearRoomSelectedMessages, (state, action) => {
      state.roomSelectedMessages = [];
    })
    .addCase(addRoomMedia, (state, action) => {
      state.messages = { ...state.messages, ...keyBy(action.payload.mediaList, "messageId") };
      state.roomMedia[action.payload.roomId][action.payload.filter] = sortBy(
        compact(
          uniq(
            concat(
              state.roomMedia[action.payload.roomId]?.[action.payload.filter],
              map(
                action.payload.mediaList.filter((m) => !m.deleted),
                "messageId"
              )
            )
          )
        )
      );
    })
    .addCase(removeRoomMedia, (state, action) => {
      state.roomMedia = omit(state.roomMedia, action.payload);
    })
    .addCase(addMessagesLoader, (state, action) => {
      state.messagesLoader[action.payload.roomId] = "init";
    })
    .addCase(editMessagesLoader, (state, action) => {
      state.messagesLoader[action.payload.roomId] = action.payload.status;
    })
    .addCase(removeMessagesLoader, (state, action) => {
      delete state.messagesLoader[action.payload.roomId];
    })
    .addCase(removeMessagesLoaders, (state, action) => {
      state.messagesLoader = omit(state.messagesLoader, action.payload);
    })
    .addCase(setScrolledMessage, (state, action) => {
      const roomId = Object.keys(action.payload)[0] as Room.AsObject["id"];
      state.scrolledMessage[roomId] = { ...state.scrolledMessage[roomId], ...Object.values(action.payload)[0] };
    })
    .addCase(setSharedMedia, (state, action) => {
      const mediaKey = Object.keys(action.payload)[0] as sharedMediaTypes;
      const omitedStatus = action.payload[mediaKey].map((item) => omit(item, "status"));
      state.sharedMedia.messages[mediaKey] = uniqBy([...state.sharedMedia.messages[mediaKey], ...omitedStatus], "messageId") as Array<RoomMessage.AsObject>;
    })
    .addCase(resetSharedMedia, (state, action) => {
      state.sharedMedia = { roomId: action.payload, messages: initialState.sharedMedia.messages };
    })
    .addCase(removeFakeMessage, (state, action) => {
      const roomMessages = state.roomMessages[action.payload.roomId];
      const messageId: RoomMessage.AsObject["messageId"] = roomMessages.map((messageId) => state.messages[messageId]).filter((message) => message?.randomId === action?.payload?.randomId)[0].messageId;
      const messages = state.messages;
      delete messages[messageId];
      state.messages = messages || state.messages;
      state.roomMessages[action.payload.roomId] = state.roomMessages[action.payload.roomId].filter((messageId) => messageId !== action.payload.randomId);
    });
});
